import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { CARD_CODES, SAVED_CARD_TYPES, US_STATES, savedCardSchema } from '@app/shared';
import type { SavedCardDto, SavedCardInput } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { CardField, useCardsAvailable } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import { useCustomerContext } from './CustomerLayout';
import { Section, yesNo } from './parts';

type Draft = Record<
  | 'nameOnCard'
  | 'cardType'
  | 'last4'
  | 'expMonth'
  | 'expYear'
  | 'line1'
  | 'line2'
  | 'city'
  | 'state'
  | 'zip',
  string
> & { isPrimary: boolean };

const MONTHS = Array.from({ length: 12 }, (_x, i) => String(i + 1).padStart(2, '0'));
const THIS_YEAR = new Date().getFullYear();
/** Twenty years ahead; an existing card's own year stays selectable. */
const yearsFor = (current: string): string[] => {
  const years = Array.from({ length: 21 }, (_x, i) => String(THIS_YEAR + i));
  return current && !years.includes(current) ? [current, ...years] : years;
};

const draftOf = (card: SavedCardDto | null, billing: Draft | null): Draft =>
  card
    ? {
        nameOnCard: card.nameOnCard,
        cardType: card.cardType,
        last4: card.last4,
        expMonth: String(card.expMonth).padStart(2, '0'),
        expYear: String(card.expYear),
        ...card.address,
        isPrimary: card.isPrimary,
      }
    : (billing ?? {
        nameOnCard: '',
        cardType: '',
        last4: '',
        expMonth: '',
        expYear: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        isPrimary: false,
      });

/** "Add New Card" and "Update Card Details". */
function CardDialog({
  customerId,
  card,
  initial,
  onClose,
}: {
  customerId: string;
  card: SavedCardDto | null;
  initial: Draft | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(card, initial));
  const [problems, setProblems] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (input: SavedCardInput) =>
      card
        ? api.put<SavedCardDto>(`/customers/${customerId}/cards/${card.id}`, input)
        : api.post<SavedCardDto>(`/customers/${customerId}/cards`, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'cards'] }),
  });

  const set = (key: keyof Draft, value: string | boolean) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setProblems((p) => ({ ...p, [key]: '' }));
  };
  const field = (key: Exclude<keyof Draft, 'isPrimary'>) => ({
    value: draft[key],
    onChange: (e: { target: { value: string } }) => set(key, e.target.value),
    error: Boolean(problems[key]),
    helperText: problems[key] || undefined,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = savedCardSchema.safeParse({
      ...draft,
      cardType: draft.cardType || undefined,
      state: draft.state || undefined,
      expMonth: draft.expMonth || undefined,
      expYear: draft.expYear || undefined,
    });
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const issue of parsed.error.issues) found[String(issue.path[0])] ??= issue.message;
      setProblems(found);
      return;
    }
    try {
      const saved = await save.mutateAsync(parsed.data);
      notify.success(
        card ? `Card ending ${saved.last4} updated.` : `Card ending ${saved.last4} added.`,
      );
      onClose();
    } catch (err) {
      const fromServer =
        err instanceof ApiError
          ? Object.fromEntries(err.details.map((d) => [d.path, d.message]))
          : {};
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    }
  };

  return (
    <FormDialog
      open
      title={card ? 'Update Card Details' : 'Add New Card'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
    >
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          required
          autoFocus
          label="Name on Card"
          slotProps={{ htmlInput: { maxLength: 60 } }}
          {...field('nameOnCard')}
        />
        <TextField select required label="Type of Card" {...field('cardType')}>
          {SAVED_CARD_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          label="Last 4 Digit"
          slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }}
          {...field('last4')}
        />
        {/* "Exp Date*" of the reference: the month and the year. */}
        <FormControl component="fieldset" required>
          <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
            Exp Date
          </FormLabel>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
            <TextField select required label="Expiry Month" {...field('expMonth')}>
              {MONTHS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <TextField select required label="Expiry Year" {...field('expYear')}>
              {yearsFor(draft.expYear).map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </FormControl>
        <TextField
          required
          label="Address 1"
          slotProps={{ htmlInput: { maxLength: 120 } }}
          {...field('line1')}
        />
        <TextField
          label="Address 2"
          slotProps={{ htmlInput: { maxLength: 120 } }}
          {...field('line2')}
        />
        <TextField
          required
          label="City"
          slotProps={{ htmlInput: { maxLength: 60 } }}
          {...field('city')}
        />
        <TextField select required label="State" {...field('state')}>
          {US_STATES.map(([code, name]) => (
            <MenuItem key={code} value={code}>
              {code} - {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          label="Zip"
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
          {...field('zip')}
        />
        <FormControlLabel
          label="Is Primary"
          control={
            <Checkbox
              checked={draft.isPrimary}
              onChange={(e) => set('isPrimary', e.target.checked)}
            />
          }
        />
      </Box>
      {problems.isPrimary ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {problems.isPrimary}
        </Alert>
      ) : null}
    </FormDialog>
  );
}

/**
 * Manage Saved Card (PC900): the cards on file for this customer, with "Add New Card" and
 * "Update Card Details". Card numbers and security codes are not collected.
 */
/** "Save card Details" (PC777) through the payment gateway's own form (switch card-payments). */
function GatewayCardDialog({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const cardRef = useRef<CardFieldHandle>(null);
  const [nameOnCard, setNameOnCard] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [problem, setProblem] = useState('');
  const save = useMutation({
    mutationFn: (paymentMethodId: string) =>
      api.post(`/customers/${customerId}/cards/gateway`, {
        paymentMethodId,
        nameOnCard,
        isPrimary,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'cards'] }),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setProblem('');
    try {
      const card = await cardRef.current?.collect();
      if (!card?.paymentMethodId) return setProblem('Enter the card');
      await save.mutateAsync(card.paymentMethodId);
      notify.success('Card saved; it can be charged and used by Auto Pay.');
      onClose();
    } catch (err) {
      setProblem(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    }
  };
  return (
    <FormDialog
      open
      title="Save a card"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      submitLabel="Save card"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField
          label="Name on Card"
          value={nameOnCard}
          onChange={(e) => setNameOnCard(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 60 } }}
        />
        <CardField ref={cardRef} error={problem} />
        <FormControlLabel
          control={
            <Checkbox checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
          }
          label="Make it the primary card (Auto Pay charges the primary card)"
        />
      </Box>
    </FormDialog>
  );
}

export function SavedCardsPage() {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const cardsAvailable = useCardsAvailable();
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [editing, setEditing] = useState<SavedCardDto | 'new' | null>(null);
  const cards = useQuery({
    queryKey: ['customers', customer.id, 'cards'],
    queryFn: () => api.get<{ data: SavedCardDto[] }>(`/customers/${customer.id}/cards`),
    select: (r) => r.data,
  });

  // A new card starts with the customer's name and mailing address.
  const billing: Draft = {
    nameOnCard: `${customer.firstName} ${customer.lastName}`,
    cardType: '',
    last4: '',
    expMonth: '',
    expYear: '',
    line1: customer.mailingAddress.line1,
    line2: customer.mailingAddress.line2,
    city: customer.mailingAddress.city,
    state: customer.mailingAddress.state,
    zip: customer.mailingAddress.zip,
    isPrimary: false,
  };

  const columns: Column<SavedCardDto>[] = [
    { key: '#', header: '#', width: 50, render: (_c, i) => i + 1 },
    { key: 'nameOnCard', header: 'Name on Card', render: (c) => c.nameOnCard },
    { key: 'cardType', header: 'Type of Card', render: (c) => c.cardType },
    { key: 'last4', header: 'Last 4 Digit', render: (c) => c.last4 },
    { key: 'expired', header: 'Is Credit Card Expired', render: (c) => yesNo(c.expired) },
    { key: 'isPrimary', header: 'Is Primary', render: (c) => yesNo(c.isPrimary) },
    ...(cardsAvailable
      ? [
          {
            key: 'chargeable',
            header: 'Can be charged',
            render: (c: SavedCardDto) => yesNo(c.chargeable),
          },
        ]
      : []),
    {
      key: 'action',
      header: 'Action',
      render: (c) => (
        <Button
          size="small"
          onClick={() => setEditing(c)}
          aria-label={`Update card ending ${c.last4}`}
        >
          Update Card Details
        </Button>
      ),
    },
  ];

  return (
    <Section
      title="Manage Saved Card"
      actions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          {cardsAvailable && can(CARD_CODES.saveCard) ? (
            <Button variant="contained" size="small" onClick={() => setGatewayOpen(true)}>
              Save a card (payment gateway)
            </Button>
          ) : null}
          <Button variant="outlined" size="small" onClick={() => setEditing('new')}>
            Add New Card
          </Button>
        </Box>
      }
    >
      <Alert severity="info" sx={{ mb: 1.5 }}>
        {cardsAvailable
          ? 'A card saved through the payment gateway can be charged and used by Auto Pay; 212Ez keeps only its type, last four digits and expiry. "Add New Card" only describes a card and cannot be charged.'
          : 'No payment gateway is connected. Card numbers and security codes are not collected, and a saved card cannot be charged.'}
      </Alert>
      <DataTable
        label="Saved cards"
        columns={columns}
        rows={cards.data}
        rowKey={(c) => c.id}
        loading={cards.isFetching}
        error={cards.error}
        onRetry={() => void cards.refetch()}
        emptyText="No Records Found!!"
      />
      {gatewayOpen ? (
        <GatewayCardDialog customerId={customer.id} onClose={() => setGatewayOpen(false)} />
      ) : null}
      {editing ? (
        <CardDialog
          customerId={customer.id}
          card={editing === 'new' ? null : editing}
          initial={billing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Section>
  );
}
